import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Chart from 'chart.js/auto';
import SwissZipsMap from '../components/SwissZipsMap';
import { formatLocalTimeHHMM } from '../utils/dateUtils';

// Custom valueLabelPlugin for Chart.js horizontal bar charts
const valueLabelPlugin = {
  id: 'valueLabelPlugin',
  afterDatasetsDraw(chart) {
    try {
      const opts = chart?.options?.plugins?.valueLabelPlugin;
      if (!opts || opts.enabled === false) return;

      const ctx = chart?.ctx;
      if (!ctx || typeof ctx.fillText !== 'function') return;

      ctx.save();
      ctx.font = '600 11px Inter, sans-serif';
      ctx.textBaseline = 'middle';

      (chart.data?.datasets || []).forEach((dataset, datasetIndex) => {
        try {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || meta.hidden) return;
          if (dataset.type !== 'bar' && chart.config.type !== 'bar') return;

          (meta.data || []).forEach((element, index) => {
            if (!element || typeof element.x !== 'number' || typeof element.y !== 'number' || isNaN(element.x) || isNaN(element.y)) return;

            const raw = dataset.data?.[index];
            const safe = typeof raw === 'number' ? raw : (raw && typeof raw.y === 'number' ? raw.y : (raw && typeof raw.x === 'number' ? raw.x : NaN));
            if (!Number.isFinite(safe)) return;

            let valueText = '';
            if (opts.formatter && typeof opts.formatter === 'function') {
              try {
                valueText = String(opts.formatter(safe, dataset, index) ?? '');
              } catch (e) {
                valueText = String(safe);
              }
            } else {
              valueText = Number.isInteger(safe) ? String(safe) : (Math.round(safe * 10) / 10).toFixed(1);
            }
            if (!valueText) return;

            const horizontal = chart.options?.indexAxis === 'y';
            ctx.fillStyle = String(opts.color || '#f8fafc');
            ctx.strokeStyle = 'rgba(15, 23, 42, 0.8)';
            ctx.lineWidth = 3;

            const x = Number(element.x);
            const y = Number(element.y);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return;

            if (horizontal) {
              ctx.textAlign = 'left';
              ctx.strokeText(valueText, x + 8, y);
              ctx.fillText(valueText, x + 8, y);
            } else {
              ctx.textAlign = 'center';
              ctx.strokeText(valueText, x, y - 10);
              ctx.fillText(valueText, x, y - 10);
            }
          });
        } catch (err) {}
      });

      ctx.restore();
    } catch (e) {}
  }
};

// Palette & Color helpers
const palette = [
  '#38bdf8', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#6366f1',
  '#14b8a6', '#eab308', '#ec4899', '#8b5cf6', '#84cc16', '#f97316',
  '#06b6d4', '#10b981', '#f43f5e', '#64748b', '#3b82f6', '#d946ef'
];

const metricColorMap = {
  stops: '#6366f1',
  km: '#22c55e',
  cost: '#ef4444',
  revenue: '#38bdf8',
  weight: '#f59e0b',
  hours: '#a855f7',
  fill: '#14b8a6',
  costPerTon: '#ec4899',
  co2e: '#10b981',
  onTime: '#22c55e',
  successRate: '#38bdf8',
  serviceTime: '#f59e0b'
};

const dayMetricOptions = [
  { value: 'stops', label: 'Stops' },
  { value: 'km', label: 'Km' },
  { value: 'cost', label: 'Coûts' },
  { value: 'revenue', label: "Chiffre d'affaires" },
  { value: 'weight', label: 'Poids' },
  { value: 'hours', label: 'Heures' },
  { value: 'fill', label: 'Taux remplissage' },
  { value: 'costPerTon', label: 'Coût / tonne' },
  { value: 'co2e', label: 'CO₂e (kg)' }
];

export const metricOptions = [
  { value: 'cost', label: 'Coût total (CHF)', unit: 'CHF', category: 'abs', lowerBetter: true },
  { value: 'cost_per_stop', label: 'Coût / Arrêt (CHF / arrêt)', unit: 'CHF/stop', category: 'ratio', lowerBetter: true },
  { value: 'cost_per_weight', label: 'Coût / Poids (CHF / tonne)', unit: 'CHF/t', category: 'ratio', lowerBetter: true },
  { value: 'cost_per_volume', label: 'Coût / Volume (CHF / m³)', unit: 'CHF/m³', category: 'ratio', lowerBetter: true },
  { value: 'cost_per_revenue', label: 'Coût / CA (% du CA)', unit: '% du CA', category: 'ratio', lowerBetter: true },
  { value: 'km', label: 'Distance totale (km)', unit: 'km', category: 'abs', lowerBetter: true },
  { value: 'km_per_stop', label: 'Distance / Arrêt (km / arrêt)', unit: 'km/stop', category: 'ratio', lowerBetter: true },
  { value: 'km_per_weight', label: 'Distance / Poids (km / tonne)', unit: 'km/t', category: 'ratio', lowerBetter: true },
  { value: 'km_per_volume', label: 'Distance / Volume (km / m³)', unit: 'km/m³', category: 'ratio', lowerBetter: true },
  { value: 'km_per_revenue', label: 'Distance / CA (km / 100 CHF CA)', unit: 'km/100CHF', category: 'ratio', lowerBetter: true },
  { value: 'co2e', label: 'Émissions CO₂e (kg)', unit: 'kg CO₂e', category: 'abs', lowerBetter: true },
  { value: 'co2e_per_stop', label: 'CO₂e / Arrêt (kg CO₂e / arrêt)', unit: 'kg/stop', category: 'ratio', lowerBetter: true },
  { value: 'co2e_per_weight', label: 'CO₂e / Poids (kg CO₂e / tonne)', unit: 'kg/t', category: 'ratio', lowerBetter: true },
  { value: 'co2e_per_volume', label: 'CO₂e / Volume (kg CO₂e / m³)', unit: 'kg/m³', category: 'ratio', lowerBetter: true },
  { value: 'co2e_per_revenue', label: 'CO₂e / CA (kg CO₂e / 100 CHF CA)', unit: 'kg/100CHF', category: 'ratio', lowerBetter: true },
  { value: 'duration', label: 'Durée totale (heures)', unit: 'h', category: 'abs', lowerBetter: true },
  { value: 'duration_per_stop', label: 'Durée / Arrêt (min / arrêt)', unit: 'min/stop', category: 'ratio', lowerBetter: true },
  { value: 'duration_per_weight', label: 'Durée / Poids (heures / tonne)', unit: 'h/t', category: 'ratio', lowerBetter: true },
  { value: 'duration_per_volume', label: 'Durée / Volume (heures / m³)', unit: 'h/m³', category: 'ratio', lowerBetter: true },
  { value: 'duration_per_revenue', label: 'Durée / CA (min / 100 CHF CA)', unit: 'min/100CHF', category: 'ratio', lowerBetter: true },
  { value: 'weight', label: 'Poids total (tonnes)', unit: 't', category: 'abs', lowerBetter: false },
  { value: 'weight_per_stop', label: 'Poids / Arrêt (kg / arrêt)', unit: 'kg/stop', category: 'ratio', lowerBetter: false },
  { value: 'volume', label: 'Volume total (m³)', unit: 'm³', category: 'abs', lowerBetter: false },
  { value: 'volume_per_stop', label: 'Volume / Arrêt (m³ / arrêt)', unit: 'm³/stop', category: 'ratio', lowerBetter: false },
  { value: 'revenue', label: "Chiffre d'affaires (CHF)", unit: 'CHF', category: 'abs', lowerBetter: false },
  { value: 'revenue_per_stop', label: 'CA / Arrêt (CHF / arrêt)', unit: 'CHF/stop', category: 'ratio', lowerBetter: false },
  { value: 'stops', label: 'Nombre de stops', unit: 'stops', category: 'abs', lowerBetter: false }
];

export const groupingOptions = [
  { value: 'mission', label: 'Par mission' },
  { value: 'round', label: 'Par tournée' },
  { value: 'day_of_week', label: 'Par jour de semaine' },
  { value: 'date', label: 'Par date' },
  { value: 'week', label: 'Par semaine' }
];

export const scatterXOptions = [
  { value: 'weight', label: 'Poids (tonnes)', unit: 't' },
  { value: 'volume', label: 'Volume (m³)', unit: 'm³' },
  { value: 'revenue', label: "Chiffre d'affaires (CHF)", unit: 'CHF' },
  { value: 'km', label: 'Distance (km)', unit: 'km' },
  { value: 'co2e', label: 'Émissions CO₂e (kg)', unit: 'kg CO₂e' },
  { value: 'duration', label: 'Durée (heures)', unit: 'h' },
  { value: 'stops', label: 'Nombre de stops', unit: 'stops' },
  { value: 'cost_per_stop', label: 'Coût / Arrêt (CHF/stop)', unit: 'CHF/stop' },
  { value: 'cost_per_weight', label: 'Coût / Poids (CHF/t)', unit: 'CHF/t' },
  { value: 'cost_per_volume', label: 'Coût / Volume (CHF/m³)', unit: 'CHF/m³' },
  { value: 'cost_per_revenue', label: 'Coût / CA (% du CA)', unit: '%' },
  { value: 'km_per_stop', label: 'Distance / Arrêt (km/stop)', unit: 'km/stop' },
  { value: 'km_per_weight', label: 'Distance / Poids (km/t)', unit: 'km/t' },
  { value: 'km_per_volume', label: 'Distance / Volume (km/m³)', unit: 'km/m³' },
  { value: 'co2e_per_stop', label: 'CO₂e / Arrêt (kg/stop)', unit: 'kg/stop' },
  { value: 'co2e_per_weight', label: 'CO₂e / Poids (kg/t)', unit: 'kg/t' },
  { value: 'duration_per_stop', label: 'Durée / Arrêt (min/stop)', unit: 'min/stop' },
  { value: 'duration_per_weight', label: 'Durée / Poids (h/t)', unit: 'h/t' }
];

export const fillRateTypeOptions = [
  { value: 'max', label: 'Taux de Remplissage Max (au départ)' },
  { value: 'integral', label: 'Taux de Remplissage Réel Intégral (trajet moyen)' }
];

export const resourceMetricOptions = [
  { value: 'cost', label: 'Coût Total (CHF)' },
  { value: 'km', label: 'Distance (km)' },
  { value: 'co2e', label: 'Émissions CO₂e (kg)' },
  { value: 'weight', label: 'Poids (t)' },
  { value: 'volume', label: 'Volume (m³)' },
  { value: 'stops', label: 'Nombre de stops' },
  { value: 'missions', label: 'Nombre de missions' }
];

// Stop Analytics Options
export const stopGroupingOptions = [
  { value: 'stop', label: 'Par arrêt' },
  { value: 'client', label: 'Par client' },
  { value: 'zip', label: 'Par code postal (NPA)' },
  { value: 'day_of_week', label: 'Par jour de semaine' },
  { value: 'date', label: 'Par date' }
];

export const stopMetricOptions = [
  { value: 'service_time_avg', label: 'Temps de service moyen (min)', unit: 'min', lowerBetter: true },
  { value: 'service_time_total', label: 'Temps de service total (heures)', unit: 'h', lowerBetter: true },
  { value: 'on_time_rate', label: 'Taux de ponctualité (% à l\'heure)', unit: '%', lowerBetter: false },
  { value: 'success_rate', label: 'Taux de succès (% livré)', unit: '%', lowerBetter: false },
  { value: 'stops_count', label: 'Nombre d\'arrêts', unit: 'arrêts', lowerBetter: false },
  { value: 'weight', label: 'Poids total (kg)', unit: 'kg', lowerBetter: false },
  { value: 'volume', label: 'Volume total (m³)', unit: 'm³', lowerBetter: false },
  { value: 'revenue', label: 'Chiffre d\'affaires (CHF)', unit: 'CHF', lowerBetter: false },
  { value: 'revenue_per_stop', label: 'CA / Arrêt (CHF / arrêt)', unit: 'CHF/stop', lowerBetter: false }
];

export const stopScatterXOptions = [
  { value: 'weight', label: 'Poids (kg)', unit: 'kg' },
  { value: 'volume', label: 'Volume (m³)', unit: 'm³' },
  { value: 'revenue', label: 'Chiffre d\'affaires (CHF)', unit: 'CHF' },
  { value: 'service_time_avg', label: 'Temps de service moyen (min)', unit: 'min' },
  { value: 'stops_count', label: 'Nombre d\'arrêts', unit: 'arrêts' },
  { value: 'on_time_rate', label: 'Taux de ponctualité (%)', unit: '%' }
];

export const stopDayMetricOptions = [
  { value: 'stops_count', label: 'Arrêts' },
  { value: 'weight', label: 'Poids (kg)' },
  { value: 'volume', label: 'Volume (m³)' },
  { value: 'revenue', label: 'Chiffre d\'affaires (CHF)' },
  { value: 'service_time_total', label: 'Temps service total (h)' }
];

export const stopDayLineOptions = [
  { value: 'on_time_rate', label: 'Taux de ponctualité (%)' },
  { value: 'success_rate', label: 'Taux de succès (%)' },
  { value: 'service_time_avg', label: 'Temps de service moyen (min)' },
  { value: 'none', label: 'Aucune courbe' }
];

const dayOfWeekNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

function formatDateLabel(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
    return `${d.getDate()} ${months[d.getMonth()]}`;
  } catch (e) {
    return dateStr;
  }
}

function getISOWeek(dateObj) {
  try {
    const d = new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return `Semaine ${weekNo} (${d.getUTCFullYear()})`;
  } catch (e) {
    return 'Semaine inconnue';
  }
}

function gradientColor01(t) {
  const clamped = Math.max(0, Math.min(1, t));
  const hue = 120 * clamped;
  return `hsl(${hue}, 75%, 52%)`;
}

function fillRateGradientColor(rate) {
  const r = parseFloat(rate || 0);
  if (r <= 100) {
    const t = Math.max(0, Math.min(1, r / 100));
    return `hsl(${120 * t}, 85%, 52%)`;
  }
  if (r <= 105) {
    const t = (r - 100) / 5;
    return `hsl(${120 - 90 * t}, 85%, 52%)`;
  }
  return `hsl(0, 85%, 52%)`;
}

function buildRankGradientColors(values, lowerIsBetter = true) {
  if (!values || values.length === 0) return [];
  const indexed = values.map((v, i) => ({ v: parseFloat(v || 0), i }));
  indexed.sort((a, b) => lowerIsBetter ? a.v - b.v : b.v - a.v);
  const colors = new Array(values.length);
  indexed.forEach((item, rank) => {
    const t = indexed.length <= 1 ? 0 : rank / (indexed.length - 1);
    colors[item.i] = gradientColor01(1 - t);
  });
  return colors;
}

function timeStringToSec(timeStr) {
  if (!timeStr) return 0;
  if (typeof timeStr === 'string' && timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1]?.slice(0, 8) || '00:00:00';
    return timeStringToSec(timePart);
  }
  const parts = String(timeStr).split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  return h * 3600 + m * 60 + s;
}

function getMetricValue(g, mKey, groupingKey, aggMode = 'avg') {
  const count = g.count || 1;
  const isMission = groupingKey === 'mission';
  const useTotal = aggMode === 'total' || isMission;

  switch (mKey) {
    case 'cost_per_stop':
      return g.totalStops > 0 ? (g.totalCost / g.totalStops) : 0;
    case 'cost_per_weight':
      return g.totalWeightTonnes > 0 ? (g.totalCost / g.totalWeightTonnes) : 0;
    case 'cost_per_volume':
      return g.totalVolumeM3 > 0 ? (g.totalCost / g.totalVolumeM3) : 0;
    case 'cost_per_revenue':
      return g.totalRevenue > 0 ? ((g.totalCost / g.totalRevenue) * 100) : 0;
    case 'km_per_stop':
      return g.totalStops > 0 ? (g.totalKm / g.totalStops) : 0;
    case 'km_per_weight':
      return g.totalWeightTonnes > 0 ? (g.totalKm / g.totalWeightTonnes) : 0;
    case 'km_per_volume':
      return g.totalVolumeM3 > 0 ? (g.totalKm / g.totalVolumeM3) : 0;
    case 'km_per_revenue':
      return g.totalRevenue > 0 ? (g.totalKm / (g.totalRevenue / 100)) : 0;
    case 'co2e_per_stop':
      return g.totalStops > 0 ? (g.totalCo2eKg / g.totalStops) : 0;
    case 'co2e_per_weight':
      return g.totalWeightTonnes > 0 ? (g.totalCo2eKg / g.totalWeightTonnes) : 0;
    case 'co2e_per_volume':
      return g.totalVolumeM3 > 0 ? (g.totalCo2eKg / g.totalVolumeM3) : 0;
    case 'co2e_per_revenue':
      return g.totalRevenue > 0 ? (g.totalCo2eKg / (g.totalRevenue / 100)) : 0;
    case 'duration_per_stop':
      return g.totalStops > 0 ? ((g.totalDurationHours * 60) / g.totalStops) : 0;
    case 'duration_per_weight':
      return g.totalWeightTonnes > 0 ? (g.totalDurationHours / g.totalWeightTonnes) : 0;
    case 'duration_per_volume':
      return g.totalVolumeM3 > 0 ? (g.totalDurationHours / g.totalVolumeM3) : 0;
    case 'duration_per_revenue':
      return g.totalRevenue > 0 ? ((g.totalDurationHours * 60) / (g.totalRevenue / 100)) : 0;
    case 'weight_per_stop':
      return g.totalStops > 0 ? (g.totalWeightKg / g.totalStops) : 0;
    case 'volume_per_stop':
      return g.totalStops > 0 ? (g.totalVolumeM3 / g.totalStops) : 0;
    case 'revenue_per_stop':
      return g.totalStops > 0 ? (g.totalRevenue / g.totalStops) : 0;
    case 'cost':
      return useTotal ? g.totalCost : (g.totalCost / count);
    case 'km':
      return useTotal ? g.totalKm : (g.totalKm / count);
    case 'co2e':
      return useTotal ? g.totalCo2eKg : (g.totalCo2eKg / count);
    case 'duration':
      return useTotal ? g.totalDurationHours : (g.totalDurationHours / count);
    case 'weight':
      return useTotal ? g.totalWeightTonnes : (g.totalWeightTonnes / count);
    case 'volume':
      return useTotal ? g.totalVolumeM3 : (g.totalVolumeM3 / count);
    case 'revenue':
      return useTotal ? g.totalRevenue : (g.totalRevenue / count);
    case 'stops':
      return useTotal ? g.totalStops : (g.totalStops / count);
    default:
      return 0;
  }
}

function computeGroupedData(rows, groupingKey, metricKey, aggMode = 'avg') {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const groupsMap = new Map();

  rows.forEach(r => {
    if (!r) return;
    let key = '';
    let label = '';
    let sortKey = 0;

    const dateStr = String(r.job_date || '');
    const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
    const validDate = !isNaN(d.getTime());

    if (groupingKey === 'mission') {
      key = `mission_${r.id}`;
      label = `${formatDateLabel(r.job_date)} · ${r.name || 'Mission'}`;
      sortKey = validDate ? d.getTime() : 0;
    } else if (groupingKey === 'round') {
      key = String(r.round_name || 'Tournée Standard');
      label = key;
      sortKey = key;
    } else if (groupingKey === 'day_of_week') {
      const dayIdx = validDate ? d.getDay() : 0;
      key = `dow_${dayIdx}`;
      label = dayOfWeekNames[dayIdx] || 'Inconnu';
      sortKey = dayIdx === 0 ? 7 : dayIdx;
    } else if (groupingKey === 'date') {
      key = dateStr;
      label = formatDateLabel(dateStr);
      sortKey = dateStr;
    } else if (groupingKey === 'week') {
      key = validDate ? getISOWeek(d) : 'Semaine inconnue';
      label = key;
      sortKey = key;
    }

    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        key,
        label,
        sortKey,
        totalCost: 0,
        totalKm: 0,
        totalCo2eKg: 0,
        totalDurationHours: 0,
        totalWeightKg: 0,
        totalWeightTonnes: 0,
        totalVolumeM3: 0,
        totalRevenue: 0,
        totalStops: 0,
        count: 0
      });
    }

    const g = groupsMap.get(key);
    g.totalCost += (r.corrected_cost || 0);
    g.totalKm += (r.corrected_length || 0);
    g.totalCo2eKg += (r.co2e_kg || 0);
    g.totalDurationHours += (r.corrected_duration || 0);
    g.totalWeightKg += (r.total_weight || 0);
    g.totalWeightTonnes += (r.weightTonnes || 0);
    g.totalVolumeM3 += (r.total_volume || 0);
    g.totalRevenue += (r.total_revenue || 0);
    g.totalStops += (r.stop_count || 0);
    g.count += 1;
  });

  const curMetricObj = metricOptions.find(m => m.value === metricKey) || metricOptions[0];
  const list = Array.from(groupsMap.values()).map(g => {
    const rawVal = getMetricValue(g, metricKey, groupingKey, aggMode);
    return {
      ...g,
      value: Math.round(rawVal * 10) / 10
    };
  });

  list.sort((a, b) => {
    if (groupingKey === 'day_of_week') return a.sortKey - b.sortKey;
    if (groupingKey === 'date') return String(a.sortKey).localeCompare(String(b.sortKey));
    if (groupingKey === 'mission') return a.sortKey - b.sortKey;
    return curMetricObj.lowerBetter !== false ? a.value - b.value : b.value - a.value;
  });

  return list;
}

// Helper: Compute grouped data for Stop / Client analytics
function computeStopGroupedData(stopRows, groupingKey, metricKey, aggMode = 'avg') {
  if (!Array.isArray(stopRows) || stopRows.length === 0) return [];
  const groupsMap = new Map();

  stopRows.forEach(s => {
    if (!s) return;
    let key = '';
    let label = '';
    let sortKey = 0;

    const dateStr = String(s.dateStr || '');
    const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
    const validDate = !isNaN(d.getTime());

    if (groupingKey === 'stop') {
      key = `stop_${s.id}`;
      label = `#${s.sequence_order || s.id} · ${s.client_name}`;
      sortKey = s.id;
    } else if (groupingKey === 'client') {
      key = String(s.client_name || 'Client inconnu');
      label = key;
      sortKey = key;
    } else if (groupingKey === 'zip') {
      key = String(s.postal_code || 'NPA inconnu');
      label = `${key} ${s.city || ''}`.trim();
      sortKey = key;
    } else if (groupingKey === 'day_of_week') {
      const dayIdx = validDate ? d.getDay() : 0;
      key = `dow_${dayIdx}`;
      label = dayOfWeekNames[dayIdx] || 'Inconnu';
      sortKey = dayIdx === 0 ? 7 : dayIdx;
    } else if (groupingKey === 'date') {
      key = dateStr;
      label = formatDateLabel(dateStr);
      sortKey = dateStr;
    }

    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        key,
        label,
        sortKey,
        totalStops: 0,
        deliveredCount: 0,
        failedCount: 0,
        onTimeCount: 0,
        totalServiceTimeMins: 0,
        totalWeightKg: 0,
        totalVolumeM3: 0,
        totalRevenue: 0,
        totalDelayMins: 0
      });
    }

    const g = groupsMap.get(key);
    g.totalStops += 1;
    if (s.isDelivered) g.deliveredCount += 1;
    if (s.isFailed) g.failedCount += 1;
    if (s.isOnTime) g.onTimeCount += 1;
    g.totalServiceTimeMins += (s.serviceTimeMins || 10);
    g.totalWeightKg += (s.weightKg || 0);
    g.totalVolumeM3 += (s.volumeM3 || 0);
    g.totalRevenue += (s.revenue || 0);
    g.totalDelayMins += (s.twDelayMins || 0);
  });

  const curMetricObj = stopMetricOptions.find(m => m.value === metricKey) || stopMetricOptions[0];

  const list = Array.from(groupsMap.values()).map(g => {
    let val = 0;
    const count = g.totalStops || 1;
    switch (metricKey) {
      case 'service_time_avg':
        val = g.totalServiceTimeMins / count;
        break;
      case 'service_time_total':
        val = g.totalServiceTimeMins / 60; // in hours
        break;
      case 'on_time_rate':
        val = (g.onTimeCount / count) * 100;
        break;
      case 'success_rate':
        val = (g.deliveredCount / count) * 100;
        break;
      case 'stops_count':
        val = g.totalStops;
        break;
      case 'weight':
        val = g.totalWeightKg;
        break;
      case 'volume':
        val = g.totalVolumeM3;
        break;
      case 'revenue':
        val = g.totalRevenue;
        break;
      case 'revenue_per_stop':
        val = g.totalRevenue / count;
        break;
      default:
        val = 0;
    }

    return {
      ...g,
      value: Math.round(val * 10) / 10,
      onTimePct: Math.round((g.onTimeCount / count) * 100),
      successPct: Math.round((g.deliveredCount / count) * 100),
      avgServiceTime: Math.round((g.totalServiceTimeMins / count) * 10) / 10
    };
  });

  list.sort((a, b) => {
    if (groupingKey === 'day_of_week') return a.sortKey - b.sortKey;
    if (groupingKey === 'date') return String(a.sortKey).localeCompare(String(b.sortKey));
    return curMetricObj.lowerBetter !== false ? a.value - b.value : b.value - a.value;
  });

  return list;
}

function format1(num, suffix = '') {
  const n = parseFloat(num);
  if (!Number.isFinite(n)) return '0' + suffix;
  const rounded = Math.round(n * 10) / 10;
  return (Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)) + suffix;
}

function formatPct(num) {
  const n = parseFloat(num);
  if (!Number.isFinite(n)) return '0%';
  return Math.round(n) + '%';
}

export default function AnalyticsPage() {
  // ----------------------------------------------------
  // SUB-TAB STATE: 'missions' | 'stops'
  // ----------------------------------------------------
  const [primarySubTab, setPrimarySubTab] = useState('missions');

  // Active view in subtabs
  const [activeMissionView, setActiveMissionView] = useState('graphiques-jour');
  const [activeStopView, setActiveStopView] = useState('stop-graphiques-jour');

  // API Data State
  const [data, setData] = useState({
    jobs: [],
    stops: [],
    trucks: [],
    drivers: [],
    rounds: [],
    clients: [],
    hubs: []
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataLoadError, setDataLoadError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const fetchAnalyticsData = useCallback(async () => {
    setIsLoadingData(true);
    setDataLoadError(null);
    try {
      const res = await fetch('/api/bootstrap?all=true');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({
        jobs: json.jobs || [],
        stops: json.stops || [],
        trucks: json.trucks || [],
        drivers: json.drivers || [],
        rounds: json.rounds || [],
        clients: json.clients || [],
        hubs: json.hubs || []
      });
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setDataLoadError(err.message || 'Erreur de chargement');
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const jobs = data.jobs;
  const stops = data.stops;
  const trucks = data.trucks;
  const drivers = data.drivers;
  const rounds = data.rounds;
  const clients = data.clients;
  const hubs = data.hubs;

  // ----------------------------------------------------
  // 1. DATA SELECTION & FILTER STATE
  // ----------------------------------------------------
  const [dateRangePreset, setDateRangePreset] = useState('ALL');
  const [startDateStr, setStartDateStr] = useState('');
  const [endDateStr, setEndDateStr] = useState('');

  const [selectedStatuses, setSelectedStatuses] = useState({
    planned: true,
    in_progress: true,
    completed: true,
    done: true
  });

  const [selectedOperationTypes, setSelectedOperationTypes] = useState({
    delivery: true,
    pickup: true
  });

  const isStopMatchingType = useCallback((stop) => {
    if (!stop) return false;
    const type = String(stop.type || 'delivery').toLowerCase().trim();
    if (type === 'pickup') {
      return !!selectedOperationTypes.pickup;
    }
    return !!selectedOperationTypes.delivery;
  }, [selectedOperationTypes]);

  const [calculationMode, setCalculationMode] = useState('hybrid');

  // Filter & Search Controls
  const [searchInput, setSearchInput] = useState('');
  const [sortSelect, setSortSelect] = useState('dateAsc');

  // Mission Analytics Controls
  const [dayBarMetrics, setDayBarMetrics] = useState({ stops: true, km: true });
  const [dayLineMetric, setDayLineMetric] = useState('cost');

  const [barMetric, setBarMetric] = useState('cost_per_weight');
  const [barGrouping, setBarGrouping] = useState('mission');

  // Scatterplot starts with "cost" (Coût total) on Y axis as requested
  const [scatterGrouping, setScatterGrouping] = useState('mission');
  const [scatterMetricY, setScatterMetricY] = useState('cost');
  const [scatterMetricX, setScatterMetricX] = useState('weight');
  const [scatterAggMode, setScatterAggMode] = useState('avg');

  const [fillRateType, setFillRateType] = useState('max');
  const [fillRateGrouping, setFillRateGrouping] = useState('mission');

  const [resourceType, setResourceType] = useState('truck');
  const [resourceMetric, setResourceMetric] = useState('cost');

  // Stop Analytics Controls
  const [stopDayBarMetric, setStopDayBarMetric] = useState({ stops_count: true, weight: true });
  const [stopDayLineMetric, setStopDayLineMetric] = useState('on_time_rate');

  const [stopBarMetric, setStopBarMetric] = useState('service_time_avg');
  const [stopBarGrouping, setStopBarGrouping] = useState('client');

  const [stopScatterGrouping, setStopScatterGrouping] = useState('client');
  const [stopScatterMetricY, setStopScatterMetricY] = useState('service_time_avg');
  const [stopScatterMetricX, setStopScatterMetricX] = useState('weight');

  const [stopAdherenceGrouping, setStopAdherenceGrouping] = useState('round');

  // Canvas Refs for Chart.js
  const chartRefs = {
    dayMixed: useRef(null),
    barAnalysis: useRef(null),
    costVsTonMission: useRef(null),
    fillRate: useRef(null),
    resourcePie: useRef(null),
    // Stops refs
    stopDayMixed: useRef(null),
    stopBarAnalysis: useRef(null),
    stopScatter: useRef(null),
    stopAdherence: useRef(null)
  };

  const chartInstances = useRef({});

  // Preset Date Helper
  const handlePresetSelect = (preset) => {
    setDateRangePreset(preset);
    const now = new Date();
    if (preset === 'ALL') {
      setStartDateStr('');
      setEndDateStr('');
    } else if (preset === 'TODAY') {
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      setStartDateStr(`${yyyy}-${mm}-${dd}`);
      setEndDateStr(`${yyyy}-${mm}-${dd}`);
    } else if (preset === 'WEEK') {
      const past = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
      setStartDateStr(past.toISOString().slice(0, 10));
      setEndDateStr(now.toISOString().slice(0, 10));
    } else if (preset === 'MONTH') {
      const past = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
      setStartDateStr(past.toISOString().slice(0, 10));
      setEndDateStr(now.toISOString().slice(0, 10));
    }
  };

  // Map raw backend jobs into unified analytics row format
  const analyticsRows = useMemo(() => {
    if (!Array.isArray(jobs)) return [];
    return jobs.map(job => {
      if (!job) return null;
      const allJobStops = Array.isArray(stops) ? stops.filter(s => s && s.job_id === job.id) : [];
      const matchingJobStops = allJobStops.filter(isStopMatchingType);
      const round = Array.isArray(rounds) ? rounds.find(r => r && r.id === job.round_id) : null;
      const truck = Array.isArray(trucks) ? trucks.find(t => t && t.id === job.truck_id) : null;
      const driver = Array.isArray(drivers) ? drivers.find(d => d && d.id === job.driver_id) : null;

      const isDone = job.status === 'completed' || job.status === 'done' || job.status === 'in_progress';
      const useReal = calculationMode === 'real' || (calculationMode === 'hybrid' && isDone);

      // Aggregates for filtered operation types (e.g. delivery only vs pickup only)
      const totalWeightKg = matchingJobStops.reduce((sum, s) => sum + parseFloat(s?.weight_kg || 0), 0);
      const weightTonnes = totalWeightKg / 1000;
      const totalVolumeM3 = matchingJobStops.reduce((sum, s) => sum + parseFloat(s?.volume_m3 || 0.5), 0);

      // Distance, duration, cost and CO2e: physical route execution metrics
      const km = parseFloat(
        useReal ? (job.real_length_km || job.estimated_length_km || 0) : (job.estimated_length_km || 0)
      );

      const durationHours = parseFloat(
        ((useReal ? (job.real_duration_sec || job.estimated_duration_sec || 0) : (job.estimated_duration_sec || 0)) / 3600).toFixed(1)
      );

      const cost = parseFloat(
        useReal ? (job.real_cost || job.estimated_cost || 0) : (job.estimated_cost || 0)
      );

      const computedStopsRevenue = matchingJobStops.reduce((sum, s) => sum + parseFloat(s?.revenue || 0), 0);
      const revenue = computedStopsRevenue > 0
        ? computedStopsRevenue
        : (matchingJobStops.length > 0
            ? Math.round((matchingJobStops.length * 25) + (km * 2.5) + (weightTonnes * 80))
            : 0);

      const maxCapKg = parseFloat(truck?.max_payload_weight_kg || 1200);

      // CO2e calculation: kms * truck.co2e_per_km
      const co2ePerKm = parseFloat(truck?.co2e_per_km !== undefined && truck?.co2e_per_km !== null ? truck.co2e_per_km : 0.25);
      const co2eKg = parseFloat((km * co2ePerKm).toFixed(2));
      const co2eTonnes = parseFloat((co2eKg / 1000).toFixed(4));

      // PHYSICAL FILL RATE: computed on ALL actual cargo carried by the vehicle,
      // strictly immune to operation type reporting filters.
      const totalPhysicalWeightKg = allJobStops.reduce((sum, s) => sum + parseFloat(s?.weight_kg || 0), 0);
      const fillRateMaxPct = maxCapKg > 0 ? Math.round((totalPhysicalWeightKg / maxCapKg) * 100) : 0;

      // Real Integral Fill Rate (average load along route segments)
      let currentLoad = totalPhysicalWeightKg;
      let loadSum = currentLoad;
      const legCount = allJobStops.length + 1;
      for (let i = 0; i < allJobStops.length; i++) {
        const sWeight = parseFloat(allJobStops[i]?.weight_kg || 0);
        currentLoad = Math.max(0, currentLoad - sWeight);
        loadSum += currentLoad;
      }
      const avgRouteLoad = legCount > 0 ? (loadSum / legCount) : 0;
      const fillRateIntegralPct = maxCapKg > 0 ? Math.round((avgRouteLoad / maxCapKg) * 100) : 0;

      const costPerTon = weightTonnes > 0 ? Math.round((cost / weightTonnes) * 10) / 10 : 0;

      const stopDate = (matchingJobStops[0] || allJobStops[0])?.delivery_date;
      const rawDateStr = job.delivery_date || stopDate || job.real_start || job.created_at || '2026-08-17';
      const dateStr = typeof rawDateStr === 'string'
        ? rawDateStr.slice(0, 10)
        : (rawDateStr instanceof Date ? rawDateStr.toISOString().slice(0, 10) : '2026-08-17');

      const rawStatus = job.status || 'planned';
      const normStatus = rawStatus === 'completed' || rawStatus === 'done' ? 'completed' : rawStatus === 'in_progress' ? 'in_progress' : 'planned';

      return {
        ...job,
        job_date: dateStr,
        name: String(job.display_name || round?.name || `Mission #${job.id}`),
        round_name: String(round?.name || 'Tournée Standard'),
        driver_name: driver ? `${driver.first_name || ''} ${driver.last_name || ''}`.trim() : 'Non assigné',
        truck_name: truck ? `${truck.plate || ''} (${truck.name || ''})`.trim() : 'Véhicule',
        stop_count: matchingJobStops.length,
        total_stops_unfiltered: allJobStops.length,
        corrected_length: Number.isFinite(km) ? km : 0,
        corrected_duration: Number.isFinite(durationHours) ? durationHours : 0,
        corrected_cost: Number.isFinite(cost) ? cost : 0,
        co2e_kg: Number.isFinite(co2eKg) ? co2eKg : 0,
        co2e_per_km: co2ePerKm,
        co2eTonnes: Number.isFinite(co2eTonnes) ? co2eTonnes : 0,
        total_weight: Number.isFinite(totalWeightKg) ? totalWeightKg : 0,
        total_weight_physical: totalPhysicalWeightKg,
        weightTonnes: Number.isFinite(weightTonnes) ? weightTonnes : 0,
        total_volume: Number.isFinite(totalVolumeM3) ? totalVolumeM3 : 0,
        total_revenue: Number.isFinite(revenue) ? revenue : 0,
        fill_rate_max: Number.isFinite(fillRateMaxPct) ? fillRateMaxPct : 0,
        fill_rate_integral: Number.isFinite(fillRateIntegralPct) ? fillRateIntegralPct : 0,
        fill_rate: Number.isFinite(fillRateMaxPct) ? fillRateMaxPct : 0,
        max_weight: Number.isFinite(maxCapKg) ? maxCapKg : 0,
        costPerTon: Number.isFinite(costPerTon) ? costPerTon : 0,
        rawStatus: normStatus,
        status: normStatus === 'completed' ? 'DONE' : normStatus === 'in_progress' ? 'ONGOING' : 'READY'
      };
    }).filter(Boolean);
  }, [jobs, stops, trucks, drivers, rounds, calculationMode, isStopMatchingType]);

  // Filtered & Sorted Mission Rows
  const filteredRows = useMemo(() => {
    const isFilteringType = !(selectedOperationTypes.delivery && selectedOperationTypes.pickup);

    let rows = analyticsRows.filter(job => {
      if (!selectedStatuses[job.rawStatus]) return false;

      // When restricting by operation type, exclude missions that only had the other type
      if (isFilteringType && job.stop_count === 0 && job.total_stops_unfiltered > 0) {
        return false;
      }

      const jobDate = new Date(job.job_date);
      if (isNaN(jobDate.getTime())) return true;

      if (startDateStr) {
        const start = new Date(startDateStr);
        if (jobDate < start) return false;
      }
      if (endDateStr) {
        const end = new Date(endDateStr);
        end.setHours(23, 59, 59, 999);
        if (jobDate > end) return false;
      }

      return true;
    });

    const q = String(searchInput || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(r =>
        [r.job_date, r.name, r.round_name, r.driver_name, r.truck_name, r.status]
          .map(v => String(v || '')).join(' ').toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => {
      if (sortSelect === 'costDesc') return (b.corrected_cost || 0) - (a.corrected_cost || 0);
      if (sortSelect === 'weightDesc') return (b.total_weight || 0) - (a.total_weight || 0);
      if (sortSelect === 'fillDesc') return (b.fill_rate_max || 0) - (a.fill_rate_max || 0);
      const dateA = String(a?.job_date || '');
      const dateB = String(b?.job_date || '');
      return sortSelect === 'dateDesc' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
    });

    return rows;
  }, [analyticsRows, selectedStatuses, startDateStr, endDateStr, searchInput, sortSelect, selectedOperationTypes]);

  // Stop Analytics Rows
  const stopAnalyticsRows = useMemo(() => {
    if (!Array.isArray(stops)) return [];
    return stops.map(stop => {
      if (!stop) return null;
      const client = Array.isArray(clients) ? clients.find(c => c && c.id === stop.client_id) : null;
      const job = Array.isArray(jobs) ? jobs.find(j => j && j.id === stop.job_id) : null;
      const round = Array.isArray(rounds) ? rounds.find(r => r && r.id === (job?.round_id || client?.default_round_id)) : null;
      const driver = Array.isArray(drivers) ? drivers.find(d => d && d.id === job?.driver_id) : null;
      const truck = Array.isArray(trucks) ? trucks.find(t => t && t.id === job?.truck_id) : null;

      const dateStr = stop.delivery_date
        ? String(stop.delivery_date).slice(0, 10)
        : (job?.delivery_date ? String(job.delivery_date).slice(0, 10) : '2026-08-17');

      const weightKg = parseFloat(stop.weight_kg || 0);
      const volumeM3 = parseFloat(stop.volume_m3 || 0);
      const revenue = parseFloat(stop.revenue || 0) || Math.round(25 + weightKg * 0.08);
      const serviceTimeMins = parseInt(stop.service_time_mins || client?.default_service_time_mins || 10, 10);

      const postalCode = String(stop.postal_code || client?.postal_code || '1000');
      const city = String(stop.city || client?.city || 'Lausanne');

      const isDelivered = stop.status === 'delivered' || stop.status === 'done';
      const isFailed = stop.status === 'failed' || stop.status === 'cancelled';
      const isPending = stop.status === 'pending' || !stop.status;

      let tws = [];
      try {
        if (Array.isArray(stop.time_windows)) tws = stop.time_windows;
        else if (typeof stop.time_windows === 'string') tws = JSON.parse(stop.time_windows);
      } catch (e) {}

      const arrivalTime = stop.actual_arrival
        ? formatLocalTimeHHMM(stop.actual_arrival, '08:00')
        : (stop.estimated_arrival ? String(stop.estimated_arrival).slice(0, 5) : '08:00');

      let isOnTime = true;
      let twDelayMins = 0;
      if (tws.length > 0) {
        const arrSec = timeStringToSec(arrivalTime);
        const hit = tws.some(tw => {
          const sSec = timeStringToSec(tw.start);
          const eSec = timeStringToSec(tw.end);
          return arrSec >= (sSec - 60) && arrSec <= (eSec + 60);
        });
        isOnTime = hit;
        if (!hit) {
          const firstTW = tws[0];
          const sSec = timeStringToSec(firstTW.start);
          const eSec = timeStringToSec(firstTW.end);
          if (arrSec > eSec) twDelayMins = Math.round((arrSec - eSec) / 60);
          else if (arrSec < sSec) twDelayMins = Math.round((arrSec - sSec) / 60);
        }
      }

      const dateObj = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
      const dayOfWeek = isNaN(dateObj.getTime()) ? 'Lundi' : dayOfWeekNames[dateObj.getDay()];

      return {
        ...stop,
        client_name: client?.name || stop.client_name || `Client #${stop.client_id}`,
        client_code: client?.code || stop.client_code || '',
        postal_code: postalCode,
        city,
        dateStr,
        dayOfWeek,
        weightKg,
        volumeM3,
        revenue,
        serviceTimeMins,
        isDelivered,
        isFailed,
        isPending,
        isOnTime,
        twDelayMins,
        arrivalTime,
        job_display_name: job?.display_name || `Mission #${stop.job_id || 'Non assignée'}`,
        round_name: round?.name || 'Tournée Standard',
        driver_name: driver ? `${driver.first_name || ''} ${driver.last_name || ''}`.trim() : 'Non assigné',
        truck_name: truck?.name || 'Non assigné'
      };
    }).filter(Boolean);
  }, [stops, clients, jobs, rounds, drivers, trucks]);

  // Filtered & Sorted Stop Rows
  const filteredStopRows = useMemo(() => {
    let rows = stopAnalyticsRows.filter(s => {
      // 1. Operation type filter (pickup vs delivery)
      if (!isStopMatchingType(s)) return false;

      const stopDate = new Date(s.dateStr);
      if (isNaN(stopDate.getTime())) return true;

      if (startDateStr) {
        const start = new Date(startDateStr);
        if (stopDate < start) return false;
      }
      if (endDateStr) {
        const end = new Date(endDateStr);
        end.setHours(23, 59, 59, 999);
        if (stopDate > end) return false;
      }

      return true;
    });

    const q = String(searchInput || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(s =>
        [s.client_name, s.client_code, s.postal_code, s.city, s.address, s.type, s.status, s.round_name, s.driver_name]
          .map(v => String(v || '')).join(' ').toLowerCase().includes(q)
      );
    }

    return rows;
  }, [stopAnalyticsRows, startDateStr, endDateStr, searchInput, isStopMatchingType]);

  // Totaux KPI metrics for Missions
  const totauxMetrics = useMemo(() => {
    const totalRounds = new Set(filteredRows.map(r => String(r.round_name || ''))).size;
    const totalMissions = filteredRows.length;
    const totalStops = filteredRows.reduce((s, r) => s + (r.stop_count || 0), 0);
    const totalKm = filteredRows.reduce((s, r) => s + (r.corrected_length || 0), 0);
    const totalCo2eKg = filteredRows.reduce((s, r) => s + (r.co2e_kg || 0), 0);
    const totalHours = filteredRows.reduce((s, r) => s + (r.corrected_duration || 0), 0);
    const totalCost = filteredRows.reduce((s, r) => s + (r.corrected_cost || 0), 0);
    const totalWeightKg = filteredRows.reduce((s, r) => s + (r.total_weight || 0), 0);
    const totalRevenue = filteredRows.reduce((s, r) => s + (r.total_revenue || 0), 0);
    const avgFillMax = totalMissions ? filteredRows.reduce((s, r) => s + (r.fill_rate_max || 0), 0) / totalMissions : 0;
    const avgFillIntegral = totalMissions ? filteredRows.reduce((s, r) => s + (r.fill_rate_integral || 0), 0) / totalMissions : 0;
    const overweightCount = filteredRows.filter(r => (r.total_weight_physical || r.total_weight) > r.max_weight && r.max_weight > 0).length;
    const totalTons = totalWeightKg / 1000;
    const avgCostTon = totalTons > 0 ? totalCost / totalTons : 0;

    const opLabel = (!selectedOperationTypes.pickup) ? 'livraisons seules' : (!selectedOperationTypes.delivery) ? 'ramasses seules' : '';

    return [
      { label: 'Tournées', value: totalRounds, sub: `${totalMissions} mission(s)` },
      { label: 'Missions', value: totalMissions, sub: `${filteredRows.filter(r => r.status !== 'DONE').length} non clôturée(s)` },
      { label: 'Stops', value: Math.round(totalStops), sub: opLabel },
      { label: 'Km', value: format1(totalKm), sub: '' },
      { label: 'Émissions CO₂e', value: format1(totalCo2eKg, ' kg'), sub: `${format1(totalCo2eKg / 1000, ' t CO₂e')}` },
      { label: 'Heures', value: format1(totalHours), sub: '' },
      { label: 'Coût total', value: format1(totalCost, ' CHF'), sub: '' },
      { label: 'Poids total', value: format1(totalTons, ' t'), sub: opLabel ? `${format1(totalWeightKg, ' kg')} (${opLabel})` : `${format1(totalWeightKg, ' kg')}` },
      { label: 'CA total', value: format1(totalRevenue, ' CHF'), sub: opLabel ? `(${opLabel})` : '' },
      { label: 'Coût / tonne', value: format1(avgCostTon, ' CHF/t'), sub: opLabel ? `sur ${opLabel}` : 'moyenne globale' },
      { label: 'Remplissage moyen (Max)', value: formatPct(avgFillMax), sub: `${formatPct(avgFillIntegral)} réel moyen (${overweightCount} surpoids)` }
    ];
  }, [filteredRows, selectedOperationTypes]);

  // Totaux KPI metrics for Stops
  const stopTotauxMetrics = useMemo(() => {
    const totalStops = filteredStopRows.length;
    const uniqueClients = new Set(filteredStopRows.map(s => s.client_name)).size;
    const uniqueZips = new Set(filteredStopRows.map(s => s.postal_code)).size;
    const deliveredCount = filteredStopRows.filter(s => s.isDelivered).length;
    const onTimeCount = filteredStopRows.filter(s => s.isOnTime).length;
    const totalServiceTimeMin = filteredStopRows.reduce((sum, s) => sum + (s.serviceTimeMins || 10), 0);
    const avgServiceTimeMin = totalStops > 0 ? totalServiceTimeMin / totalStops : 0;
    const totalWeightKg = filteredStopRows.reduce((sum, s) => sum + (s.weightKg || 0), 0);
    const totalVolumeM3 = filteredStopRows.reduce((sum, s) => sum + (s.volumeM3 || 0), 0);
    const totalRevenue = filteredStopRows.reduce((sum, s) => sum + (s.revenue || 0), 0);
    const onTimePct = totalStops > 0 ? (onTimeCount / totalStops) * 100 : 0;
    const successPct = totalStops > 0 ? (deliveredCount / totalStops) * 100 : 0;

    const pickupCount = filteredStopRows.filter(s => (s.type || '').toLowerCase() === 'pickup').length;
    const deliveryCount = totalStops - pickupCount;
    let stopsSub = `${pickupCount} ramasses`;
    if (!selectedOperationTypes.pickup) stopsSub = 'Livraisons seules';
    else if (!selectedOperationTypes.delivery) stopsSub = 'Ramasses seules';
    else stopsSub = `${pickupCount} ramasse(s), ${deliveryCount} livr.`;

    return [
      { label: 'Arrêts totaux', value: totalStops, sub: stopsSub },
      { label: 'Clients uniques', value: uniqueClients, sub: 'destinataires actifs' },
      { label: 'Codes postaux (NPA)', value: uniqueZips, sub: 'zones desservies' },
      { label: 'Ponctualité globale', value: formatPct(onTimePct), sub: `${totalStops - onTimeCount} hors plage` },
      { label: 'Taux de succès', value: formatPct(successPct), sub: `${deliveredCount} livrés / collectés` },
      { label: 'Temps service moyen', value: format1(avgServiceTimeMin, ' min'), sub: `Total ${(totalServiceTimeMin / 60).toFixed(1)} h` },
      { label: 'Poids total', value: format1(totalWeightKg / 1000, ' t'), sub: `${format1(totalWeightKg, ' kg')}` },
      { label: 'Volume total', value: format1(totalVolumeM3, ' m³'), sub: '' },
      { label: 'CA total', value: format1(totalRevenue, ' CHF'), sub: '' },
      { label: 'CA / Arrêt', value: format1(totalStops > 0 ? totalRevenue / totalStops : 0, ' CHF'), sub: 'moyenne / stop' }
    ];
  }, [filteredStopRows, selectedOperationTypes]);

  // ----------------------------------------------------
  // CHART RENDERING (Chart.js)
  // ----------------------------------------------------
  useEffect(() => {
    const renderChart = (key, config) => {
      if (chartInstances.current[key]) {
        try {
          chartInstances.current[key].destroy();
        } catch (e) {}
        chartInstances.current[key] = null;
      }
      if (chartRefs[key]?.current) {
        try {
          chartInstances.current[key] = new Chart(chartRefs[key].current, config);
        } catch (err) {
          console.error(`Chart render error [${key}]:`, err);
        }
      }
    };

    if (primarySubTab === 'missions') {
      // 1. Vue par jour (Day Mixed)
      const byDayMap = new Map();
      filteredRows.forEach(r => {
        const d = String(r.job_date || '');
        if (!d) return;
        if (!byDayMap.has(d)) {
          byDayMap.set(d, { date: d, stops: 0, km: 0, cost: 0, co2e: 0, revenue: 0, weight: 0, hours: 0, fillSum: 0, count: 0 });
        }
        const item = byDayMap.get(d);
        item.stops += (r.stop_count || 0);
        item.km += (r.corrected_length || 0);
        item.cost += (r.corrected_cost || 0);
        item.co2e += (r.co2e_kg || 0);
        item.revenue += (r.total_revenue || 0);
        item.weight += (r.weightTonnes || 0);
        item.hours += (r.corrected_duration || 0);
        item.fillSum += (r.fill_rate_max || 0);
        item.count += 1;
      });

      const byDay = Array.from(byDayMap.values()).sort((a, b) => a.date.localeCompare(b.date));
      const selectedBarKeys = Object.keys(dayBarMetrics).filter(k => dayBarMetrics[k]);

      renderChart('dayMixed', {
        type: 'bar',
        data: {
          labels: byDay.map(x => formatDateLabel(x.date)),
          datasets: [
            ...selectedBarKeys.map(k => ({
              type: 'bar',
              label: dayMetricOptions.find(o => o.value === k)?.label || k,
              data: byDay.map(x => k === 'fill' ? (x.count ? Math.round(x.fillSum / x.count) : 0) : k === 'weight' ? Math.round(x.weight * 10) / 10 : Math.round((x[k] || 0) * 10) / 10),
              backgroundColor: `${metricColorMap[k] || '#38bdf8'}88`,
              borderColor: metricColorMap[k] || '#38bdf8',
              borderWidth: 1,
              yAxisID: 'y'
            })),
            ...(dayLineMetric !== 'none' ? [{
              type: 'line',
              label: dayMetricOptions.find(o => o.value === dayLineMetric)?.label || dayLineMetric,
              data: byDay.map(x => dayLineMetric === 'fill' ? (x.count ? Math.round(x.fillSum / x.count) : 0) : dayLineMetric === 'weight' ? Math.round(x.weight * 10) / 10 : Math.round((x[dayLineMetric] || 0) * 10) / 10),
              borderColor: metricColorMap[dayLineMetric] || '#ef4444',
              backgroundColor: metricColorMap[dayLineMetric] || '#ef4444',
              tension: 0.25,
              pointRadius: 4,
              yAxisID: 'y1'
            }] : [])
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ticks: { color: '#94a3b8' }, grid: { color: '#334155' } },
            y: { ticks: { color: '#94a3b8' }, grid: { color: '#334155' }, beginAtZero: true },
            y1: { ticks: { color: '#94a3b8' }, position: 'right', grid: { drawOnChartArea: false }, beginAtZero: true }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            valueLabelPlugin: { enabled: false }
          }
        }
      });

      // 2. Merged Multi-Metric Bar Chart
      const curMetricObj = metricOptions.find(m => m.value === barMetric) || metricOptions[0];
      const groupedBarData = computeGroupedData(filteredRows, barGrouping, barMetric);
      const barColors = buildRankGradientColors(groupedBarData.map(r => r.value), curMetricObj.lowerBetter !== false);

      renderChart('barAnalysis', {
        type: 'bar',
        plugins: [valueLabelPlugin],
        data: {
          labels: groupedBarData.map(r => String(r.label || '')),
          datasets: [{
            label: String(curMetricObj.label || ''),
            data: groupedBarData.map(r => r.value),
            backgroundColor: barColors,
            borderColor: barColors,
            borderWidth: 1
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true,
              title: {
                display: true,
                text: String(curMetricObj.unit || ''),
                color: '#94a3b8'
              }
            },
            y: { ticks: { color: '#f8fafc', font: { weight: '600' } }, grid: { color: '#334155' } }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const item = groupedBarData[context.dataIndex];
                  const countText = barGrouping !== 'mission' ? ` (${item?.count || 1} mission(s))` : '';
                  return ` ${curMetricObj.label}: ${context.parsed.x} ${curMetricObj.unit}${countText}`;
                }
              }
            },
            valueLabelPlugin: {
              enabled: true,
              formatter: (val) => `${val} ${curMetricObj.unit}`
            }
          }
        }
      });

      // 3. Dynamic Scatterplot / Nuage de points
      const curScatterYObj = metricOptions.find(m => m.value === scatterMetricY) || metricOptions[0];
      const curScatterXObj = scatterXOptions.find(m => m.value === scatterMetricX) || scatterXOptions[0];

      const groupedScatterData = computeGroupedData(filteredRows, scatterGrouping, scatterMetricY, scatterAggMode);
      const scatterPoints = groupedScatterData.map(g => {
        const xVal = Math.round(getMetricValue(g, scatterMetricX, scatterGrouping, scatterAggMode) * 100) / 100;
        const yVal = g.value;
        const radius = Math.min(24, Math.max(6, Math.round(Math.sqrt(g.totalStops || 1) * 3.5)));
        return {
          x: Number.isFinite(xVal) ? xVal : 0,
          y: Number.isFinite(yVal) ? yVal : 0,
          r: radius,
          label: String(g.label || ''),
          count: g.count || 1,
          stops: g.totalStops || 0
        };
      });

      const scatterColors = buildRankGradientColors(scatterPoints.map(p => p.y), curScatterYObj.lowerBetter !== false);
      const modeLabel = scatterGrouping === 'mission' ? '' : (scatterAggMode === 'total' ? ' (Total cumulé)' : ' (Moyenne / mission)');

      renderChart('costVsTonMission', {
        type: 'bubble',
        data: {
          datasets: [{
            label: `${curScatterYObj.label} vs ${curScatterXObj.label}${modeLabel}`,
            data: scatterPoints,
            backgroundColor: scatterColors,
            borderColor: scatterColors
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true,
              title: {
                display: true,
                text: `${curScatterXObj.label} (${curScatterXObj.unit})${modeLabel}`,
                color: '#94a3b8',
                font: { weight: '700' }
              }
            },
            y: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true,
              title: {
                display: true,
                text: `${curScatterYObj.label} (${curScatterYObj.unit})${modeLabel}`,
                color: '#94a3b8',
                font: { weight: '700' }
              }
            }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            valueLabelPlugin: { enabled: false },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const pt = context.raw;
                  if (!pt) return '';
                  return [
                    `📍 ${pt.label || ''}`,
                    `  Axe Y (${curScatterYObj.label}): ${pt.y} ${curScatterYObj.unit}${modeLabel}`,
                    `  Axe X (${curScatterXObj.label}): ${pt.x} ${curScatterXObj.unit}${modeLabel}`,
                    `  Volume/Stops: ${pt.stops} stop(s) · ${pt.count} mission(s)`
                  ];
                }
              }
            }
          }
        }
      });

      // 4. Taux de remplissage
      const fillData = filteredRows.map(r => ({
        label: `${formatDateLabel(r.job_date)} · ${r.name}`,
        fill: fillRateType === 'integral' ? r.fill_rate_integral : r.fill_rate_max,
        weightKg: r.total_weight,
        maxKg: r.max_weight
      })).sort((a, b) => b.fill - a.fill);

      const fillColors = fillData.map(d => fillRateGradientColor(d.fill));

      renderChart('fillRate', {
        type: 'bar',
        plugins: [valueLabelPlugin],
        data: {
          labels: fillData.map(d => d.label),
          datasets: [{
            label: fillRateType === 'integral' ? 'Remplissage Intégral Moyen (%)' : 'Remplissage Max (%)',
            data: fillData.map(d => d.fill),
            backgroundColor: fillColors,
            borderColor: fillColors,
            borderWidth: 1
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true,
              max: Math.max(120, Math.max(...fillData.map(d => d.fill), 100)),
              title: { display: true, text: 'Taux de Remplissage (%)', color: '#94a3b8' }
            },
            y: { ticks: { color: '#f8fafc', font: { weight: '600' } }, grid: { color: '#334155' } }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            valueLabelPlugin: {
              enabled: true,
              formatter: (val) => `${val}%`
            }
          }
        }
      });

      // 5. Unified Resource Pie / Doughnut Chart
      const resMap = new Map();
      filteredRows.forEach(r => {
        const name = String(resourceType === 'truck' ? (r.truck_name || 'Véhicule') : (r.driver_name || 'Chauffeur'));
        let val = 0;
        if (resourceMetric === 'cost') val = r.corrected_cost || 0;
        else if (resourceMetric === 'km') val = r.corrected_length || 0;
        else if (resourceMetric === 'co2e') val = r.co2e_kg || 0;
        else if (resourceMetric === 'weight') val = r.weightTonnes || 0;
        else if (resourceMetric === 'volume') val = r.total_volume || 0;
        else if (resourceMetric === 'stops') val = r.stop_count || 0;
        else if (resourceMetric === 'missions') val = 1;

        resMap.set(name, (resMap.get(name) || 0) + (Number.isFinite(val) ? val : 0));
      });

      const resList = Array.from(resMap.entries())
        .map(([name, val]) => ({ name, val: Math.round(val * 10) / 10 }))
        .sort((a, b) => b.val - a.val);

      renderChart('resourcePie', {
        type: 'doughnut',
        data: {
          labels: resList.map(r => r.name),
          datasets: [{
            data: resList.map(r => r.val),
            backgroundColor: palette.slice(0, resList.length),
            borderColor: '#0f172a',
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right', labels: { color: '#f8fafc', font: { size: 11 } } },
            valueLabelPlugin: { enabled: false }
          }
        }
      });
    } else {
      // ----------------------------------------------------
      // STOPS ANALYTICS CHARTS RENDERING
      // ----------------------------------------------------
      // 1. Stop Timeline by Day
      const stopDayMap = new Map();
      filteredStopRows.forEach(s => {
        const d = String(s.dateStr || '');
        if (!d) return;
        if (!stopDayMap.has(d)) {
          stopDayMap.set(d, {
            date: d,
            stops_count: 0,
            weight: 0,
            volume: 0,
            revenue: 0,
            service_time_total: 0,
            onTimeCount: 0,
            deliveredCount: 0
          });
        }
        const item = stopDayMap.get(d);
        item.stops_count += 1;
        item.weight += (s.weightKg || 0);
        item.volume += (s.volumeM3 || 0);
        item.revenue += (s.revenue || 0);
        item.service_time_total += ((s.serviceTimeMins || 10) / 60); // in hours
        if (s.isOnTime) item.onTimeCount += 1;
        if (s.isDelivered) item.deliveredCount += 1;
      });

      const stopByDay = Array.from(stopDayMap.values()).sort((a, b) => a.date.localeCompare(b.date));
      const selectedStopBarKeys = Object.keys(stopDayBarMetric).filter(k => stopDayBarMetric[k]);

      renderChart('stopDayMixed', {
        type: 'bar',
        data: {
          labels: stopByDay.map(x => formatDateLabel(x.date)),
          datasets: [
            ...selectedStopBarKeys.map(k => ({
              type: 'bar',
              label: stopDayMetricOptions.find(o => o.value === k)?.label || k,
              data: stopByDay.map(x => Math.round((x[k] || 0) * 10) / 10),
              backgroundColor: `${metricColorMap[k] || '#38bdf8'}88`,
              borderColor: metricColorMap[k] || '#38bdf8',
              borderWidth: 1,
              yAxisID: 'y'
            })),
            ...(stopDayLineMetric !== 'none' ? [{
              type: 'line',
              label: stopDayLineOptions.find(o => o.value === stopDayLineMetric)?.label || stopDayLineMetric,
              data: stopByDay.map(x => {
                if (stopDayLineMetric === 'on_time_rate') return x.stops_count ? Math.round((x.onTimeCount / x.stops_count) * 100) : 0;
                if (stopDayLineMetric === 'success_rate') return x.stops_count ? Math.round((x.deliveredCount / x.stops_count) * 100) : 0;
                if (stopDayLineMetric === 'service_time_avg') return x.stops_count ? Math.round(((x.service_time_total * 60) / x.stops_count) * 10) / 10 : 0;
                return 0;
              }),
              borderColor: metricColorMap[stopDayLineMetric] || '#22c55e',
              backgroundColor: metricColorMap[stopDayLineMetric] || '#22c55e',
              tension: 0.25,
              pointRadius: 4,
              yAxisID: 'y1'
            }] : [])
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { ticks: { color: '#94a3b8' }, grid: { color: '#334155' } },
            y: { ticks: { color: '#94a3b8' }, grid: { color: '#334155' }, beginAtZero: true },
            y1: { ticks: { color: '#94a3b8' }, position: 'right', grid: { drawOnChartArea: false }, beginAtZero: true }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            valueLabelPlugin: { enabled: false }
          }
        }
      });

      // 2. Stop Comparative Bar Chart
      const curStopMetricObj = stopMetricOptions.find(m => m.value === stopBarMetric) || stopMetricOptions[0];
      const groupedStopBarData = computeStopGroupedData(filteredStopRows, stopBarGrouping, stopBarMetric);
      const stopBarColors = buildRankGradientColors(groupedStopBarData.map(r => r.value), curStopMetricObj.lowerBetter !== false);

      renderChart('stopBarAnalysis', {
        type: 'bar',
        plugins: [valueLabelPlugin],
        data: {
          labels: groupedStopBarData.map(r => String(r.label || '')),
          datasets: [{
            label: String(curStopMetricObj.label || ''),
            data: groupedStopBarData.map(r => r.value),
            backgroundColor: stopBarColors,
            borderColor: stopBarColors,
            borderWidth: 1
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true,
              title: { display: true, text: String(curStopMetricObj.unit || ''), color: '#94a3b8' }
            },
            y: { ticks: { color: '#f8fafc', font: { weight: '600' } }, grid: { color: '#334155' } }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const item = groupedStopBarData[context.dataIndex];
                  return ` ${curStopMetricObj.label}: ${context.parsed.x} ${curStopMetricObj.unit} (${item?.totalStops || 1} arrêt(s))`;
                }
              }
            },
            valueLabelPlugin: {
              enabled: true,
              formatter: (val) => `${val} ${curStopMetricObj.unit}`
            }
          }
        }
      });

      // 3. Stop Scatterplot
      const curStopScatterYObj = stopMetricOptions.find(m => m.value === stopScatterMetricY) || stopMetricOptions[0];
      const curStopScatterXObj = stopScatterXOptions.find(m => m.value === stopScatterMetricX) || stopScatterXOptions[0];

      const groupedStopScatterData = computeStopGroupedData(filteredStopRows, stopScatterGrouping, stopScatterMetricY);
      const stopScatterPoints = groupedStopScatterData.map(g => {
        let xVal = 0;
        const count = g.totalStops || 1;
        if (stopScatterMetricX === 'weight') xVal = g.totalWeightKg;
        else if (stopScatterMetricX === 'volume') xVal = g.totalVolumeM3;
        else if (stopScatterMetricX === 'revenue') xVal = g.totalRevenue;
        else if (stopScatterMetricX === 'service_time_avg') xVal = g.totalServiceTimeMins / count;
        else if (stopScatterMetricX === 'stops_count') xVal = g.totalStops;
        else if (stopScatterMetricX === 'on_time_rate') xVal = (g.onTimeCount / count) * 100;

        const yVal = g.value;
        const radius = Math.min(24, Math.max(6, Math.round(Math.sqrt(g.totalStops || 1) * 4)));
        return {
          x: Math.round(xVal * 10) / 10,
          y: Math.round(yVal * 10) / 10,
          r: radius,
          label: String(g.label || ''),
          stops: g.totalStops || 0
        };
      });

      const stopScatterColors = buildRankGradientColors(stopScatterPoints.map(p => p.y), curStopScatterYObj.lowerBetter !== false);

      renderChart('stopScatter', {
        type: 'bubble',
        data: {
          datasets: [{
            label: `${curStopScatterYObj.label} vs ${curStopScatterXObj.label}`,
            data: stopScatterPoints,
            backgroundColor: stopScatterColors,
            borderColor: stopScatterColors
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true,
              title: {
                display: true,
                text: `${curStopScatterXObj.label} (${curStopScatterXObj.unit})`,
                color: '#94a3b8',
                font: { weight: '700' }
              }
            },
            y: {
              ticks: { color: '#94a3b8' },
              grid: { color: '#334155' },
              beginAtZero: true,
              title: {
                display: true,
                text: `${curStopScatterYObj.label} (${curStopScatterYObj.unit})`,
                color: '#94a3b8',
                font: { weight: '700' }
              }
            }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            valueLabelPlugin: { enabled: false },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const pt = context.raw;
                  if (!pt) return '';
                  return [
                    `📍 ${pt.label || ''}`,
                    `  Axe Y (${curStopScatterYObj.label}): ${pt.y} ${curStopScatterYObj.unit}`,
                    `  Axe X (${curStopScatterXObj.label}): ${pt.x} ${curStopScatterXObj.unit}`,
                    `  Volume: ${pt.stops} arrêt(s)`
                  ];
                }
              }
            }
          }
        }
      });

      // 4. Stop Success & Adherence Breakdown
      const adherenceData = computeStopGroupedData(filteredStopRows, stopAdherenceGrouping, 'on_time_rate');

      renderChart('stopAdherence', {
        type: 'bar',
        plugins: [valueLabelPlugin],
        data: {
          labels: adherenceData.map(d => d.label),
          datasets: [
            {
              label: 'À l\'heure (%)',
              data: adherenceData.map(d => d.onTimePct),
              backgroundColor: 'rgba(34, 197, 94, 0.75)',
              borderColor: '#22c55e',
              borderWidth: 1
            },
            {
              label: 'Hors plage (%)',
              data: adherenceData.map(d => 100 - d.onTimePct),
              backgroundColor: 'rgba(239, 68, 68, 0.75)',
              borderColor: '#ef4444',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { stacked: true, ticks: { color: '#94a3b8' }, grid: { color: '#334155' } },
            y: { stacked: true, ticks: { color: '#94a3b8' }, grid: { color: '#334155' }, max: 100, beginAtZero: true }
          },
          plugins: {
            legend: { labels: { color: '#f8fafc' } },
            valueLabelPlugin: {
              enabled: true,
              formatter: (val) => val > 5 ? `${Math.round(val)}%` : ''
            }
          }
        }
      });
    }

    return () => {
      Object.values(chartInstances.current).forEach(c => c && c.destroy && c.destroy());
    };
  }, [
    primarySubTab,
    filteredRows,
    filteredStopRows,
    dayBarMetrics,
    dayLineMetric,
    barMetric,
    barGrouping,
    scatterGrouping,
    scatterMetricX,
    scatterMetricY,
    scatterAggMode,
    fillRateType,
    fillRateGrouping,
    resourceType,
    resourceMetric,
    stopDayBarMetric,
    stopDayLineMetric,
    stopBarMetric,
    stopBarGrouping,
    stopScatterGrouping,
    stopScatterMetricY,
    stopScatterMetricX,
    stopAdherenceGrouping
  ]);

  const exportPng = (key) => {
    const chart = chartInstances.current[key];
    if (chart && typeof chart.toBase64Image === 'function') {
      const link = document.createElement('a');
      link.href = chart.toBase64Image('image/png', 1);
      link.download = `${key}.png`;
      link.click();
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const curBarMetricObj = metricOptions.find(m => m.value === barMetric) || metricOptions[0];
  const curBarGroupingObj = groupingOptions.find(g => g.value === barGrouping) || groupingOptions[0];

  const curScatterYObj = metricOptions.find(m => m.value === scatterMetricY) || metricOptions[0];
  const curScatterXObj = scatterXOptions.find(m => m.value === scatterMetricX) || scatterXOptions[0];
  const curScatterGroupingObj = groupingOptions.find(g => g.value === scatterGrouping) || groupingOptions[0];

  const curFillRateTypeObj = fillRateTypeOptions.find(t => t.value === fillRateType) || fillRateTypeOptions[0];
  const curFillRateGroupingObj = groupingOptions.find(g => g.value === fillRateGrouping) || groupingOptions[0];

  const curResMetricObj = resourceMetricOptions.find(m => m.value === resourceMetric) || resourceMetricOptions[0];

  const curStopBarMetricObj = stopMetricOptions.find(m => m.value === stopBarMetric) || stopMetricOptions[0];
  const curStopBarGroupingObj = stopGroupingOptions.find(g => g.value === stopBarGrouping) || stopGroupingOptions[0];

  const curStopScatterYObj = stopMetricOptions.find(m => m.value === stopScatterMetricY) || stopMetricOptions[0];
  const curStopScatterXObj = stopScatterXOptions.find(m => m.value === stopScatterMetricX) || stopScatterXOptions[0];
  const curStopScatterGroupingObj = stopGroupingOptions.find(g => g.value === stopScatterGrouping) || stopGroupingOptions[0];

  // Resource ranked breakdown calculation for right-side display
  const resourceRankingList = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      const name = String(resourceType === 'truck' ? (r.truck_name || 'Véhicule') : (r.driver_name || 'Chauffeur'));
      let val = 0;
      if (resourceMetric === 'cost') val = r.corrected_cost || 0;
      else if (resourceMetric === 'km') val = r.corrected_length || 0;
      else if (resourceMetric === 'co2e') val = r.co2e_kg || 0;
      else if (resourceMetric === 'weight') val = r.weightTonnes || 0;
      else if (resourceMetric === 'volume') val = r.total_volume || 0;
      else if (resourceMetric === 'stops') val = r.stop_count || 0;
      else if (resourceMetric === 'missions') val = 1;

      if (!map.has(name)) {
        map.set(name, { name, val: 0, missions: 0 });
      }
      const entry = map.get(name);
      entry.val += Number.isFinite(val) ? val : 0;
      entry.missions += 1;
    });

    const list = Array.from(map.values())
      .map(item => ({
        name: item.name,
        val: Math.round(Number(item.val || 0) * 10) / 10,
        missions: item.missions
      }))
      .sort((a, b) => b.val - a.val);

    const grandTotal = list.reduce((s, i) => s + i.val, 0);
    return list.map((item, idx) => ({
      ...item,
      color: palette[idx % palette.length],
      pct: grandTotal > 0 ? Math.round((item.val / grandTotal) * 1000) / 10 : 0
    }));
  }, [filteredRows, resourceType, resourceMetric]);

  // Stop ranked breakdown for right-side display
  const stopRankingList = useMemo(() => {
    const dataList = computeStopGroupedData(filteredStopRows, stopBarGrouping, stopBarMetric);
    const grandTotal = dataList.reduce((s, i) => s + (i.value || 0), 0);
    return dataList.slice(0, 15).map((item, idx) => ({
      ...item,
      color: palette[idx % palette.length],
      pct: grandTotal > 0 ? Math.round((item.value / grandTotal) * 1000) / 10 : 0
    }));
  }, [filteredStopRows, stopBarGrouping, stopBarMetric]);

  return (
    <div style={{ width: '100%', height: '100%', overflowY: 'auto', background: '#0f172a', color: '#f8fafc', padding: '16px 20px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ERROR BANNER IF ANY */}
      {dataLoadError && (
        <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', color: '#fca5a5', fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>⚠️ {dataLoadError}</span>
          <button onClick={fetchAnalyticsData} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '0.72rem', cursor: 'pointer', fontWeight: '700' }}>Réessayer</button>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* 1. REPACKED COMPACT FILTER & CONFIGURATION CARD      */}
      {/* ---------------------------------------------------- */}
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '12px',
        padding: '14px 18px',
        marginBottom: '16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
      }}>
        {/* ROW 1: DATES, STATUSES, OPERATION TYPES & CALCULATION MODE */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '16px', alignItems: 'flex-start' }}>
          
          {/* DATETIME RANGE & PRESETS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.70rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              📅 Période Temporelle
            </label>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="date"
                value={startDateStr}
                onChange={e => { setStartDateStr(e.target.value); setDateRangePreset('CUSTOM'); }}
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#fff', padding: '5px 8px', borderRadius: '6px', fontSize: '0.76rem', outline: 'none', flex: 1 }}
              />
              <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>à</span>
              <input
                type="date"
                value={endDateStr}
                onChange={e => { setEndDateStr(e.target.value); setDateRangePreset('CUSTOM'); }}
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#fff', padding: '5px 8px', borderRadius: '6px', fontSize: '0.76rem', outline: 'none', flex: 1 }}
              />
            </div>

            <div style={{ display: 'flex', gap: '5px', marginTop: '1px' }}>
              {['ALL', 'TODAY', 'WEEK', 'MONTH'].map(preset => (
                <button
                  key={preset}
                  onClick={() => handlePresetSelect(preset)}
                  style={{
                    background: dateRangePreset === preset ? '#38bdf8' : '#0f172a',
                    color: dateRangePreset === preset ? '#0f172a' : '#94a3b8',
                    border: '1px solid ' + (dateRangePreset === preset ? '#38bdf8' : '#334155'),
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.67rem',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  {preset === 'ALL' ? 'Tout' : preset === 'TODAY' ? "Aujourd'hui" : preset === 'WEEK' ? '7 Derniers Jours' : 'Ce Mois'}
                </button>
              ))}
            </div>
          </div>

          {/* JOB STATUS FILTERS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.70rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              🚦 Statut des Missions
            </label>

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                { key: 'planned', label: 'Planifiées', color: '#f59e0b' },
                { key: 'in_progress', label: 'En cours', color: '#38bdf8' },
                { key: 'completed', label: 'Clôturées', color: '#22c55e' }
              ].map(st => (
                <label
                  key={st.key}
                  style={{
                    background: selectedStatuses[st.key] ? 'rgba(56, 189, 248, 0.1)' : '#0f172a',
                    border: `1px solid ${selectedStatuses[st.key] ? st.color : '#334155'}`,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    color: selectedStatuses[st.key] ? '#f8fafc' : '#94a3b8'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedStatuses[st.key]}
                    onChange={e => setSelectedStatuses({ ...selectedStatuses, [st.key]: e.target.checked })}
                    style={{ accentColor: st.color }}
                  />
                  <span>{st.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* OPERATION TYPE FILTERS (PICKUP / DELIVERY) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.70rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              📦 Types de flux
            </label>

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                { key: 'delivery', label: 'Livraisons', color: '#38bdf8' },
                { key: 'pickup', label: 'Ramasses', color: '#f59e0b' }
              ].map(op => {
                const isChecked = !!selectedOperationTypes[op.key];
                return (
                  <label
                    key={op.key}
                    style={{
                      background: isChecked ? 'rgba(56, 189, 248, 0.1)' : '#0f172a',
                      border: `1px solid ${isChecked ? op.color : '#334155'}`,
                      padding: '4px 9px',
                      borderRadius: '6px',
                      fontSize: '0.72rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      color: isChecked ? '#f8fafc' : '#64748b',
                      userSelect: 'none',
                      transition: 'all 0.15s ease'
                    }}
                    title={op.key === 'delivery' ? 'Inclure les livraisons' : 'Inclure les ramasses / collectes'}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={e => {
                        const nextVal = e.target.checked;
                        if (!nextVal && Object.values(selectedOperationTypes).filter(Boolean).length <= 1) {
                          return;
                        }
                        setSelectedOperationTypes(prev => ({
                          ...prev,
                          [op.key]: nextVal
                        }));
                      }}
                      style={{ accentColor: op.color, cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: isChecked ? '700' : '500' }}>{op.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* CALCULATION MODE (ESTIMATED / REAL / HYBRID) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '0.70rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ⚙️ Valeurs Analysées
            </label>

            <div style={{ background: '#0f172a', borderRadius: '6px', padding: '2px', display: 'flex', border: '1px solid #334155' }}>
              {[
                { id: 'estimated', label: 'Estimées' },
                { id: 'real', label: 'Réelles' },
                { id: 'hybrid', label: 'Hybrides' }
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setCalculationMode(mode.id)}
                  style={{
                    flex: 1,
                    background: calculationMode === mode.id ? '#38bdf8' : 'transparent',
                    color: calculationMode === mode.id ? '#0f172a' : '#94a3b8',
                    border: 'none',
                    padding: '5px 6px',
                    borderRadius: '5px',
                    fontSize: '0.70rem',
                    fontWeight: '800',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* ROW 2: SEARCH, SORT & REFRESH REPACKED INSIDE CARD */}
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid #334155',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, maxWidth: '650px' }}>
            <input
              type="text"
              placeholder={primarySubTab === 'missions' ? "Rechercher mission, tournée, chauffeur, camion..." : "Rechercher arrêt, client, ville, code postal..."}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '5px 10px', fontSize: '0.76rem', flex: 1 }}
            />

            {primarySubTab === 'missions' && (
              <select value={sortSelect} onChange={e => setSortSelect(e.target.value)} style={{ background: '#0f172a', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '5px 8px', fontSize: '0.74rem' }}>
                <option value="dateAsc">Tri : date croissante</option>
                <option value="dateDesc">Tri : date décroissante</option>
                <option value="costDesc">Tri : coût décroissant</option>
                <option value="weightDesc">Tri : poids décroissant</option>
                <option value="fillDesc">Tri : remplissage décroissant</option>
              </select>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontSize: '0.74rem', color: '#38bdf8', background: '#0f172a', padding: '4px 10px', borderRadius: '6px', fontWeight: '700', border: '1px solid #334155' }}>
              {primarySubTab === 'missions'
                ? `${filteredRows.length} mission(s) retenue(s)`
                : `${filteredStopRows.length} arrêt(s) retenu(s)`
              }
            </div>

            <button
              onClick={fetchAnalyticsData}
              disabled={isLoadingData}
              style={{
                background: '#0f172a',
                border: '1px solid #334155',
                color: '#38bdf8',
                borderRadius: '6px',
                padding: '4px 10px',
                fontSize: '0.74rem',
                fontWeight: '700',
                cursor: isLoadingData ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Rafraîchir les données"
            >
              <span style={{ display: 'inline-block', animation: isLoadingData ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
              <span>{isLoadingData ? '...' : 'Rafraîchir'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* 2. TOP TOTALS KPI TILES BAR (NO CARD TITLE)          */}
      {/* ---------------------------------------------------- */}
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '12px',
        padding: '12px',
        marginBottom: '16px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px' }}>
          {(primarySubTab === 'missions' ? totauxMetrics : stopTotauxMetrics).map((m, idx) => (
            <div key={idx} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '8px 10px' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.67rem', textTransform: 'uppercase', fontWeight: '700' }}>{m.label}</div>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', margin: '3px 0', color: '#f8fafc' }}>{m.value}</div>
              {m.sub && <div style={{ color: '#94a3b8', fontSize: '0.67rem' }}>{m.sub}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* ---------------------------------------------------- */}
      {/* 3. PRIMARY SUB-TAB SWITCHER                          */}
      {/* ---------------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <button
          onClick={() => setPrimarySubTab('missions')}
          style={{
            background: primarySubTab === 'missions' ? '#38bdf8' : '#1e293b',
            color: primarySubTab === 'missions' ? '#0f172a' : '#94a3b8',
            border: '1px solid ' + (primarySubTab === 'missions' ? '#38bdf8' : '#334155'),
            padding: '7px 16px',
            borderRadius: '8px',
            fontSize: '0.82rem',
            fontWeight: '800',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.15s ease'
          }}
        >
          <span>🚚</span>
          <span>Analyse par mission</span>
        </button>

        <button
          onClick={() => setPrimarySubTab('stops')}
          style={{
            background: primarySubTab === 'stops' ? '#38bdf8' : '#1e293b',
            color: primarySubTab === 'stops' ? '#0f172a' : '#94a3b8',
            border: '1px solid ' + (primarySubTab === 'stops' ? '#38bdf8' : '#334155'),
            padding: '7px 16px',
            borderRadius: '8px',
            fontSize: '0.82rem',
            fontWeight: '800',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.15s ease'
          }}
        >
          <span>📍</span>
          <span>Analyse par arrêt & client</span>
        </button>
      </div>

      {/* ---------------------------------------------------- */}
      {/* 4. SUB-TAB 1: ANALYSE PAR MISSION                    */}
      {/* ---------------------------------------------------- */}
      {primarySubTab === 'missions' && (
        <>
          {/* NAVIGATION MENU JUST ABOVE VUE PAR JOUR (EXCLUDING TOTAUX) */}
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid #334155',
            borderRadius: '10px',
            padding: '6px 8px',
            marginBottom: '14px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px'
          }}>
            {[
              { id: 'graphiques-jour', label: 'Vue par Jour' },
              { id: 'graphiques-analyse', label: 'Analyse Métriques' },
              { id: 'nuage-points', label: 'Nuage de Points' },
              { id: 'remplissage', label: 'Taux de Remplissage' },
              { id: 'ressources', label: 'Analyse Ressources' },
              { id: 'non-cloturees', label: 'Non clôturées' },
              { id: 'missions-table', label: 'Liste Missions' }
            ].map(nav => (
              <button
                key={nav.id}
                onClick={() => {
                  setActiveMissionView(nav.id);
                  scrollToSection(nav.id);
                }}
                style={{
                  background: activeMissionView === nav.id ? 'rgba(56, 189, 248, 0.15)' : '#1e293b',
                  border: '1px solid ' + (activeMissionView === nav.id ? '#38bdf8' : '#334155'),
                  color: activeMissionView === nav.id ? '#38bdf8' : '#f8fafc',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.73rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                {nav.label}
              </button>
            ))}
          </div>

          {/* SECTION 1: VUE PAR JOUR */}
          <div id="graphiques-jour" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Vue par jour</h2>
              <button onClick={() => exportPng('dayMixed')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Barres</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {dayMetricOptions.map(opt => (
                  <label key={opt.value} style={{ background: '#1e293b', border: '1px solid #334155', padding: '3px 8px', borderRadius: '4px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!dayBarMetrics[opt.value]}
                      onChange={e => setDayBarMetrics({ ...dayBarMetrics, [opt.value]: e.target.checked })}
                      style={{ accentColor: metricColorMap[opt.value] || '#38bdf8' }}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Courbe (axe droit) :</span>
                <select
                  value={dayLineMetric}
                  onChange={e => setDayLineMetric(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '3px 8px', fontSize: '0.72rem' }}
                >
                  <option value="none">Aucune courbe</option>
                  {dayMetricOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: '320px', width: '100%', position: 'relative' }}>
              <canvas ref={chartRefs.dayMixed} />
            </div>
          </div>

          {/* SECTION 2: ANALYSE MÉTRIQUES */}
          <div id="graphiques-analyse" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Analyse comparative</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => exportPng('barAnalysis')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
              </div>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Métrique :</span>
                <select
                  value={barMetric}
                  onChange={e => setBarMetric(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {metricOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Regroupement :</span>
                <select
                  value={barGrouping}
                  onChange={e => setBarGrouping(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {groupingOptions.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: Math.max(340, Math.min(800, computeGroupedData(filteredRows, barGrouping, barMetric).length * 24 + 80)), width: '100%', position: 'relative' }}>
              <canvas ref={chartRefs.barAnalysis} />
            </div>
          </div>

          {/* SECTION 3: NUAGE DE POINTS (SCATTERPLOT) */}
          <div id="nuage-points" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Nuage de points</h2>
              <button onClick={() => exportPng('costVsTonMission')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Axe Y :</span>
                <select
                  value={scatterMetricY}
                  onChange={e => setScatterMetricY(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {metricOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Axe X :</span>
                <select
                  value={scatterMetricX}
                  onChange={e => setScatterMetricX(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {scatterXOptions.map(x => (
                    <option key={x.value} value={x.value}>{x.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Regroupement :</span>
                <select
                  value={scatterGrouping}
                  onChange={e => setScatterGrouping(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {groupingOptions.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>

              {scatterGrouping !== 'mission' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Calcul :</span>
                  <select
                    value={scatterAggMode}
                    onChange={e => setScatterAggMode(e.target.value)}
                    style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                  >
                    <option value="avg">Moyenne par mission</option>
                    <option value="total">Total cumulé</option>
                  </select>
                </div>
              )}
            </div>

            <div style={{ height: '360px', width: '100%', position: 'relative' }}>
              <canvas ref={chartRefs.costVsTonMission} />
            </div>
          </div>

          {/* SECTION 4: TAUX DE REMPLISSAGE */}
          <div id="remplissage" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Taux de remplissage</h2>
              <button onClick={() => exportPng('fillRate')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Mode :</span>
                <select
                  value={fillRateType}
                  onChange={e => setFillRateType(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {fillRateTypeOptions.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: Math.max(340, Math.min(800, filteredRows.length * 24 + 80)), width: '100%', position: 'relative' }}>
              <canvas ref={chartRefs.fillRate} />
            </div>
          </div>

          {/* SECTION 5: ANALYSE RESSOURCES */}
          <div id="ressources" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Analyse des ressources</h2>
              <button onClick={() => exportPng('resourcePie')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Ressource :</span>
                <select
                  value={resourceType}
                  onChange={e => setResourceType(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  <option value="truck">Véhicules (Camions)</option>
                  <option value="driver">Chauffeurs</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Métrique :</span>
                <select
                  value={resourceMetric}
                  onChange={e => setResourceMetric(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {resourceMetricOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px', alignItems: 'center' }}>
              <div style={{ height: '320px', position: 'relative' }}>
                <canvas ref={chartRefs.resourcePie} />
              </div>

              <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', maxHeight: '320px', overflowY: 'auto' }}>
                <div style={{ fontSize: '0.76rem', fontWeight: '700', marginBottom: '8px', color: '#f8fafc' }}>
                  Classement par {curResMetricObj.label}
                </div>
                {resourceRankingList.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #334155', fontSize: '0.72rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color }} />
                      <span style={{ fontWeight: '600', color: '#f8fafc' }}>{item.name}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: '700', color: '#38bdf8' }}>{item.val}</span>
                      <span style={{ color: '#94a3b8', marginLeft: '6px', fontSize: '0.68rem' }}>({item.pct}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SECTION 6: NON CLÔTURÉES */}
          <div id="non-cloturees" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', marginBottom: '12px' }}>Missions non clôturées</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                <thead>
                  <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155', color: '#94a3b8', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Date</th>
                    <th style={{ padding: '8px' }}>Mission</th>
                    <th style={{ padding: '8px' }}>Tournée</th>
                    <th style={{ padding: '8px' }}>Chauffeur</th>
                    <th style={{ padding: '8px' }}>Camion</th>
                    <th style={{ padding: '8px' }}>{!selectedOperationTypes.pickup ? 'Stops livr.' : !selectedOperationTypes.delivery ? 'Stops ram.' : 'Stops'}</th>
                    <th style={{ padding: '8px' }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.filter(r => r.status !== 'DONE').map((r, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(51, 65, 85, 0.4)' }}>
                      <td style={{ padding: '8px' }}>{formatDateLabel(r.job_date)}</td>
                      <td style={{ padding: '8px', fontWeight: '600' }}>{r.name}</td>
                      <td style={{ padding: '8px' }}>{r.round_name}</td>
                      <td style={{ padding: '8px' }}>{r.driver_name}</td>
                      <td style={{ padding: '8px' }}>{r.truck_name}</td>
                      <td style={{ padding: '8px' }}>{r.stop_count}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ background: r.status === 'ONGOING' ? 'rgba(56, 189, 248, 0.15)' : 'rgba(245, 158, 11, 0.15)', color: r.status === 'ONGOING' ? '#38bdf8' : '#f59e0b', padding: '2px 6px', borderRadius: '4px', fontWeight: '700', fontSize: '0.68rem' }}>
                          {r.status === 'ONGOING' ? 'En cours' : 'Planifiée'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* SECTION 7: TABLEAU COMPLET DES MISSIONS */}
          <div id="missions-table" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', marginBottom: '12px' }}>Liste complète des missions</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                <thead>
                  <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155', color: '#94a3b8', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Date</th>
                    <th style={{ padding: '8px' }}>Mission</th>
                    <th style={{ padding: '8px' }}>Chauffeur</th>
                    <th style={{ padding: '8px' }}>Camion</th>
                    <th style={{ padding: '8px' }}>{!selectedOperationTypes.pickup ? 'Stops livr.' : !selectedOperationTypes.delivery ? 'Stops ram.' : 'Stops'}</th>
                    <th style={{ padding: '8px' }}>Km</th>
                    <th style={{ padding: '8px' }}>CO₂e</th>
                    <th style={{ padding: '8px' }}>{!selectedOperationTypes.pickup ? 'Poids livr. (t)' : !selectedOperationTypes.delivery ? 'Poids ram. (t)' : 'Poids (t)'}</th>
                    <th style={{ padding: '8px' }}>Coût</th>
                    <th style={{ padding: '8px' }}>{!selectedOperationTypes.pickup ? 'CA livr.' : !selectedOperationTypes.delivery ? 'CA ram.' : 'CA'}</th>
                    <th style={{ padding: '8px' }}>Remplissage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(51, 65, 85, 0.4)' }}>
                      <td style={{ padding: '8px' }}>{formatDateLabel(r.job_date)}</td>
                      <td style={{ padding: '8px', fontWeight: '600' }}>{r.name}</td>
                      <td style={{ padding: '8px' }}>{r.driver_name}</td>
                      <td style={{ padding: '8px' }}>{r.truck_name}</td>
                      <td style={{ padding: '8px' }}>{r.stop_count}</td>
                      <td style={{ padding: '8px' }}>{r.corrected_length} km</td>
                      <td style={{ padding: '8px' }}>{r.co2e_kg} kg</td>
                      <td style={{ padding: '8px' }}>{r.weightTonnes} t</td>
                      <td style={{ padding: '8px', fontWeight: '600', color: '#38bdf8' }}>{r.corrected_cost} CHF</td>
                      <td style={{ padding: '8px' }}>{r.total_revenue} CHF</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ color: fillRateGradientColor(r.fill_rate_max), fontWeight: '700' }}>
                          {r.fill_rate_max}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ---------------------------------------------------- */}
      {/* 5. SUB-TAB 2: ANALYSE PAR ARRÊT & CLIENT             */}
      {/* ---------------------------------------------------- */}
      {primarySubTab === 'stops' && (
        <>
          {/* NAVIGATION MENU JUST ABOVE VUE PAR JOUR POUR LES ARRÊTS */}
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid #334155',
            borderRadius: '10px',
            padding: '6px 8px',
            marginBottom: '14px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px'
          }}>
            {[
              { id: 'stop-graphiques-jour', label: 'Vue par Jour' },
              { id: 'stop-comparative', label: 'Analyse Comparative' },
              { id: 'stop-carte', label: 'Carte Interactive (NPA & Clients)' },
              { id: 'stop-nuage-points', label: 'Nuage de Points' },
              { id: 'stop-adherence', label: 'Ponctualité & Succès' },
              { id: 'stop-table', label: 'Liste des Arrêts' }
            ].map(nav => (
              <button
                key={nav.id}
                onClick={() => {
                  setActiveStopView(nav.id);
                  scrollToSection(nav.id);
                }}
                style={{
                  background: activeStopView === nav.id ? 'rgba(56, 189, 248, 0.15)' : '#1e293b',
                  border: '1px solid ' + (activeStopView === nav.id ? '#38bdf8' : '#334155'),
                  color: activeStopView === nav.id ? '#38bdf8' : '#f8fafc',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.73rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                {nav.label}
              </button>
            ))}
          </div>

          {/* STOP SECTION 1: VUE PAR JOUR */}
          <div id="stop-graphiques-jour" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Timeline quotidienne des arrêts & livraisons</h2>
              <button onClick={() => exportPng('stopDayMixed')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Barres</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {stopDayMetricOptions.map(opt => (
                  <label key={opt.value} style={{ background: '#1e293b', border: '1px solid #334155', padding: '3px 8px', borderRadius: '4px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!stopDayBarMetric[opt.value]}
                      onChange={e => setStopDayBarMetric({ ...stopDayBarMetric, [opt.value]: e.target.checked })}
                      style={{ accentColor: metricColorMap[opt.value] || '#38bdf8' }}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Courbe (axe droit) :</span>
                <select
                  value={stopDayLineMetric}
                  onChange={e => setStopDayLineMetric(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '3px 8px', fontSize: '0.72rem' }}
                >
                  {stopDayLineOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: '320px', width: '100%', position: 'relative' }}>
              <canvas ref={chartRefs.stopDayMixed} />
            </div>
          </div>

          {/* STOP SECTION 2: ANALYSE COMPARATIVE (PAR ARRÊT, PAR CLIENT, PAR NPA, PAR JOUR) */}
          <div id="stop-comparative" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Analyse comparative des arrêts, clients & zones NPA</h2>
              <button onClick={() => exportPng('stopBarAnalysis')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Métrique :</span>
                <select
                  value={stopBarMetric}
                  onChange={e => setStopBarMetric(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {stopMetricOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Regroupement :</span>
                <select
                  value={stopBarGrouping}
                  onChange={e => setStopBarGrouping(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {stopGroupingOptions.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '16px' }}>
              <div style={{ height: Math.max(340, Math.min(800, computeStopGroupedData(filteredStopRows, stopBarGrouping, stopBarMetric).length * 24 + 80)), position: 'relative' }}>
                <canvas ref={chartRefs.stopBarAnalysis} />
              </div>

              <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', maxHeight: '420px', overflowY: 'auto' }}>
                <div style={{ fontSize: '0.76rem', fontWeight: '700', marginBottom: '8px', color: '#f8fafc' }}>
                  Classement ({curStopBarGroupingObj.label}) · {curStopBarMetricObj.label}
                </div>
                {stopRankingList.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #334155', fontSize: '0.72rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color }} />
                      <span style={{ fontWeight: '600', color: '#f8fafc' }}>{item.label}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: '700', color: '#38bdf8' }}>{item.value} {curStopBarMetricObj.unit}</span>
                      <span style={{ color: '#94a3b8', marginLeft: '6px', fontSize: '0.67rem' }}>({item.totalStops} arrêts)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* STOP SECTION 3: CARTE INTERACTIVE (NPA & CLIENTS) */}
          <div id="stop-carte" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>
                  Carte interactive · Zones postales (NPA) & Localisation des clients
                </h2>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
                  Visualisation géographique des densités, des performances et des points de livraison avec polygones 4 chiffres (Suisse)
                </p>
              </div>
            </div>

            <SwissZipsMap
              stops={filteredStopRows}
              clients={clients}
              hubs={hubs}
              rounds={rounds}
            />
          </div>

          {/* STOP SECTION 4: NUAGE DE POINTS (SCATTERPLOT ARRÊTS & CLIENTS) */}
          <div id="stop-nuage-points" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Nuage de points arrêts & clients</h2>
              <button onClick={() => exportPng('stopScatter')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Axe Y :</span>
                <select
                  value={stopScatterMetricY}
                  onChange={e => setStopScatterMetricY(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {stopMetricOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Axe X :</span>
                <select
                  value={stopScatterMetricX}
                  onChange={e => setStopScatterMetricX(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {stopScatterXOptions.map(x => (
                    <option key={x.value} value={x.value}>{x.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Regroupement :</span>
                <select
                  value={stopScatterGrouping}
                  onChange={e => setStopScatterGrouping(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  {stopGroupingOptions.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ height: '360px', width: '100%', position: 'relative' }}>
              <canvas ref={chartRefs.stopScatter} />
            </div>
          </div>

          {/* STOP SECTION 4: PONCTUALITÉ & TAUX DE SUCCÈS */}
          <div id="stop-adherence" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', margin: 0 }}>Respect des plages horaires & taux de succès</h2>
              <button onClick={() => exportPng('stopAdherence')} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 10px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}>Export PNG</button>
            </div>

            <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', padding: '10px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#94a3b8' }}>Regroupement :</span>
                <select
                  value={stopAdherenceGrouping}
                  onChange={e => setStopAdherenceGrouping(e.target.value)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem' }}
                >
                  <option value="round">Par tournée</option>
                  <option value="client">Par client</option>
                  <option value="zip">Par code postal (NPA)</option>
                  <option value="day_of_week">Par jour de semaine</option>
                </select>
              </div>
            </div>

            <div style={{ height: '340px', width: '100%', position: 'relative' }}>
              <canvas ref={chartRefs.stopAdherence} />
            </div>
          </div>

          {/* STOP SECTION 5: TABLEAU COMPLET DES ARRÊTS */}
          <div id="stop-table" style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
            <h2 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc', marginBottom: '12px' }}>Liste complète des arrêts</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                <thead>
                  <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155', color: '#94a3b8', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Date</th>
                    <th style={{ padding: '8px' }}>Stop #</th>
                    <th style={{ padding: '8px' }}>Client</th>
                    <th style={{ padding: '8px' }}>NPA / Ville</th>
                    <th style={{ padding: '8px' }}>Type</th>
                    <th style={{ padding: '8px' }}>Arrivée</th>
                    <th style={{ padding: '8px' }}>Ponctualité</th>
                    <th style={{ padding: '8px' }}>Temps service</th>
                    <th style={{ padding: '8px' }}>Poids</th>
                    <th style={{ padding: '8px' }}>CA</th>
                    <th style={{ padding: '8px' }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStopRows.map((s, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(51, 65, 85, 0.4)' }}>
                      <td style={{ padding: '8px' }}>{formatDateLabel(s.dateStr)}</td>
                      <td style={{ padding: '8px', fontWeight: '700', color: '#38bdf8' }}>#{s.sequence_order || s.id}</td>
                      <td style={{ padding: '8px', fontWeight: '600' }}>{s.client_name}</td>
                      <td style={{ padding: '8px' }}>{s.postal_code} {s.city}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ background: s.type === 'pickup' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(56, 189, 248, 0.15)', color: s.type === 'pickup' ? '#f59e0b' : '#38bdf8', padding: '2px 6px', borderRadius: '4px', fontWeight: '700', fontSize: '0.67rem' }}>
                          {s.type === 'pickup' ? 'Ramasse' : 'Livraison'}
                        </span>
                      </td>
                      <td style={{ padding: '8px' }}>{s.arrivalTime}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ color: s.isOnTime ? '#22c55e' : '#ef4444', fontWeight: '700' }}>
                          {s.isOnTime ? '✓ À l\'heure' : `✗ Retard (${s.twDelayMins}m)`}
                        </span>
                      </td>
                      <td style={{ padding: '8px' }}>{s.serviceTimeMins} min</td>
                      <td style={{ padding: '8px' }}>{s.weightKg} kg</td>
                      <td style={{ padding: '8px', fontWeight: '600', color: '#38bdf8' }}>{s.revenue} CHF</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{ background: s.isDelivered ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)', color: s.isDelivered ? '#22c55e' : '#f59e0b', padding: '2px 6px', borderRadius: '4px', fontWeight: '700', fontSize: '0.67rem' }}>
                          {s.isDelivered ? 'Livré' : s.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
